import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import io from 'socket.io-client';
import { Phone, PhoneOff, Volume2, VolumeX, AlertTriangle, Mic, Clock, MessageSquare } from 'lucide-react';
import botService from '../api';
import dashboardService from '../../customerServiceDashboard/api';

/**
 * VoiceCallbackPanel — Phase 3: Live AI voice callback
 *
 * Customer voice is captured via microphone and transcribed in real-time.
 * The bot follows a scripted conversation (see VOICE_SCRIPT) and speaks
 * each line via TTS, advancing one step for every customer utterance.
 */

const BOT_GREETING =
  "Hi Miss Wallace, this is the CAP Credit Union virtual assistant calling you back. " +
  "Before I continue, for your security, please confirm the month and day of your birth.";

// Sequential script — one entry per expected customer turn
const VOICE_SCRIPT = [
  {
    // Customer says birth date
    response:
      "Thank you. I've confirmed your identity and I'm continuing from your secure chat session. " +
      "We were reviewing your checking account balance and recent transactions. " +
      "Would you like me to repeat the last five checking transactions?",
  },
  {
    // Customer says yes / please
    response:
      "Certainly. Your five most recent checking transactions are: " +
      "Fuel Station for $48.22 today, Grocery Market for $126.83 yesterday, " +
      "Payroll Deposit of $2,184.00 yesterday, Coffee Shop for $6.91 yesterday, " +
      "and Streaming Service for $18.99 two days ago.",
  },
  {
    // Customer asks about balance before payroll
    response: "Your balance before the payroll deposit posted was $1,242.18.",
  },
  {
    // Customer says okay / anything else new
    response: "One moment while I check for any newly posted activity.",
    followUp:
      "I do see a new debit card transaction that posted moments ago for $742.63 at Electro World Online. " +
      "I also see that this merchant is outside your normal spending pattern and location profile. " +
      "Did you authorize this charge?",
    followUpDelay: 2200,
  },
  {
    // Customer says no / did not authorize
    response:
      "I'm sorry. This may be fraudulent activity. " +
      "I can connect you now to a fraud specialist and pass along everything we've already covered " +
      "so you do not have to repeat yourself. Would you like me to do that?",
    setFraud: true,
  },
  {
    // Customer says yes / right now
    response: "Understood. I'm connecting you to a CAP Credit Union fraud specialist now.",
    transfer: true,
  },
];

const FRAUD_KEYWORDS = [
  'did not authorize', "didn't authorize", 'fraud', 'not mine',
  'unauthorized', 'strange charge', 'unknown charge', 'i did not do that',
  'no', 'oh my god',
];

const SPEAKER_STYLES = {
  'AI Voice': { bg: 'rgba(124,58,237,0.07)', border: 'rgba(124,58,237,0.18)', text: '#7c3aed' },
  Customer:   { bg: 'rgba(3,105,161,0.07)',  border: 'rgba(3,105,161,0.18)',  text: '#0369a1' },
};

const BotIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <line x1="12" y1="7" x2="12" y2="11" />
    <circle cx="8.5" cy="16" r="1" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="16" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const VoiceCallbackPanel = ({ onTransferToSpecialist, onTranscriptUpdate, onCallStatusChange, chatHistory }) => {
  const [lines, setLines]               = useState([]);
  const [interimLine, setInterimLine]   = useState('');
  const [isSpeaking, setIsSpeaking]     = useState(false);
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [isRecording, setIsRecording]   = useState(false);
  const [callStatus, setCallStatus]     = useState('idle'); // idle | active | ended
  const [ttsEnabled, setTtsEnabled]     = useState(true);
  const [showFraudBanner, setShowFraudBanner] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const linesRef              = useRef([]);
  const transcriptRef         = useRef('');
  const socketRef             = useRef(null);
  const audioContextRef       = useRef(null);
  const mediaStreamRef        = useRef(null);
  const processorRef          = useRef(null);
  const isSpeakingRef         = useRef(false);
  const callTimerRef          = useRef(null);
  const updateIntervalRef     = useRef(null);
  const bottomRef             = useRef(null);
  const fraudDetectedRef      = useRef(false);
  const ttsEnabledRef         = useRef(ttsEnabled);
  const pendingResponseRef    = useRef(null);  // queues customer lines spoken while bot is talking
  const triggerBotResponseRef = useRef(null);  // stable ref to avoid stale closures in queue
  const scriptStepRef         = useRef(0);     // current position in VOICE_SCRIPT
  const ttsCacheRef           = useRef({});    // text -> AudioBlob, look-ahead pre-fetch

  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);

  // Pre-fetch a single TTS line into cache (non-blocking, silent on failure)
  const prefetchTTS = useCallback(async (text) => {
    if (!text || ttsCacheRef.current[text]) return;
    try {
      const result = await botService.generateTextToSpeech(text);
      if (result.success && result.audioBlob) ttsCacheRef.current[text] = result.audioBlob;
    } catch {}
  }, []);

  // Parse web chat history into display lines.
  // Groups continuation lines (e.g. multi-line transaction lists) under the current speaker.
  const chatHistoryLines = useMemo(() => {
    if (!chatHistory) return [];
    const entries = [];
    let currentSpeaker = null;
    let currentLines = [];
    chatHistory.split('\n').forEach(raw => {
      const match = raw.match(/^(Agent|Customer):\s*(.*)/);
      if (match) {
        if (currentSpeaker) entries.push({ speaker: currentSpeaker, text: currentLines.join('\n') });
        currentSpeaker = match[1];
        currentLines = [match[2]];
      } else if (currentSpeaker) {
        currentLines.push(raw);
      }
    });
    if (currentSpeaker) entries.push({ speaker: currentSpeaker, text: currentLines.join('\n') });
    return entries.filter(e => e.text.trim()).map((e, i) => ({ ...e, id: `chat-${i}` }));
  }, [chatHistory]);

  // Auto-start recording immediately on mount
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (hasAutoStarted.current) return;
    hasAutoStarted.current = true;
    handleStartCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [lines, interimLine]);

  // Call timer
  useEffect(() => {
    if (isRecording) {
      callTimerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    } else {
      clearInterval(callTimerRef.current);
    }
    return () => clearInterval(callTimerRef.current);
  }, [isRecording]);

  const formatDuration = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const appendLine = useCallback((speaker, text) => {
    const ts = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const line = { speaker, text, ts, id: Date.now() + Math.random() };
    linesRef.current = [...linesRef.current, line];
    setLines([...linesRef.current]);
    transcriptRef.current += `\n${speaker}: ${text}`;
    onTranscriptUpdate?.(transcriptRef.current.trim());
  }, [onTranscriptUpdate]);

  // Speak a single line via TTS and append to transcript
  const speakLine = useCallback(async (text) => {
    appendLine('AI Voice', text);
    setIsSpeaking(true);
    if (ttsEnabledRef.current) {
      try {
        // Use cached blob if available (look-ahead hit), otherwise fetch now
        let blob = ttsCacheRef.current[text];
        if (!blob) {
          const result = await botService.generateTextToSpeech(text);
          blob = result.success ? result.audioBlob : null;
          if (blob) ttsCacheRef.current[text] = blob;
        }
        // While this line plays, pre-fetch the next script step in the background
        const nextStep = VOICE_SCRIPT[scriptStepRef.current];
        if (nextStep) {
          prefetchTTS(nextStep.response);
          if (nextStep.followUp) prefetchTTS(nextStep.followUp);
        }
        if (blob) await botService.playAudioBlob(blob);
      } catch { /* TTS unavailable — continue silently */ }
    }
    setIsSpeaking(false);
  }, [appendLine, prefetchTTS]);

  const triggerBotResponse = useCallback(async (customerText) => {
    if (isSpeakingRef.current) {
      // Bot is already speaking — queue the latest customer line
      pendingResponseRef.current = customerText;
      return;
    }
    isSpeakingRef.current = true;
    pendingResponseRef.current = null;

    // Fraud detection banner
    if (!fraudDetectedRef.current) {
      const lower = customerText.toLowerCase();
      if (FRAUD_KEYWORDS.some(k => lower.includes(k))) {
        fraudDetectedRef.current = true;
        setShowFraudBanner(true);
      }
    }

    const step = VOICE_SCRIPT[scriptStepRef.current];
    if (!step) {
      // Script exhausted — nothing more to say
      isSpeakingRef.current = false;
      return;
    }
    scriptStepRef.current += 1;

    // Set fraud banner before speaking if this step flags it
    if (step.setFraud && !fraudDetectedRef.current) {
      fraudDetectedRef.current = true;
      setShowFraudBanner(true);
    }

    // Speak primary response
    await speakLine(step.response);

    // Optional: brief pause then follow-up line (e.g. "One moment..." → fraud alert)
    if (step.followUp) {
      setIsBotThinking(true);
      await new Promise(resolve => setTimeout(resolve, step.followUpDelay ?? 1500));
      setIsBotThinking(false);
      await speakLine(step.followUp);
    }

    // Transfer to specialist after speaking
    if (step.transfer) {
      setTimeout(() => onTransferToSpecialist?.(), 1800);
    }

    isSpeakingRef.current = false;

    // Drain queue — respond to any customer line that arrived while bot was speaking
    if (pendingResponseRef.current) {
      const next = pendingResponseRef.current;
      pendingResponseRef.current = null;
      triggerBotResponseRef.current?.(next);
    }
  }, [speakLine, onTransferToSpecialist]);

  // Keep a stable ref so the queue drain above never calls a stale closure
  useEffect(() => { triggerBotResponseRef.current = triggerBotResponse; }, [triggerBotResponse]);

  // Socket — connect on mount, persists across call start/stop
  useEffect(() => {
    const socketUrl = dashboardService.getTranscriptionSocketUrl();
    socketRef.current = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    const socket = socketRef.current;
    socket.on('transcription', (data) => {
      if (!data?.text) return;
      if (data.text.startsWith('##')) {
        const cleanLine = data.text.replace(/^##\s*/, '');
        setInterimLine('');
        appendLine('Customer', cleanLine);
        triggerBotResponse(cleanLine); // queues if bot is currently speaking
      } else if (data.text.startsWith('>>')) {
        setInterimLine(data.text.replace(/^>>\s*/, ''));
      }
    });

    return () => socket.disconnect();
  }, [appendLine, triggerBotResponse]);

  const playGreeting = useCallback(async () => {
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    appendLine('AI Voice', BOT_GREETING);
    if (ttsEnabledRef.current) {
      try {
        const result = await botService.generateTextToSpeech(BOT_GREETING);
        if (result.success && result.audioBlob) await botService.playAudioBlob(result.audioBlob);
      } catch { /* silent */ }
    }
    setIsSpeaking(false);
    isSpeakingRef.current = false;
  }, [appendLine]);

  const handleStartCall = useCallback(async () => {
    if (isRecording) return;
    setIsRecording(true);
    setCallStatus('active');
    setCallDuration(0);
    setLines([]);
    setShowFraudBanner(false);
    linesRef.current = [];
    transcriptRef.current = '';
    fraudDetectedRef.current = false;
    scriptStepRef.current = 0;
    onCallStatusChange?.(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      await audioContext.audioWorklet.addModule('/audio-processor.js');
      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');
      workletNode.port.onmessage = ({ data }) => {
        // Don't send audio while the bot is speaking — prevents overlap and echo
        if (isSpeakingRef.current) return;
        const downsampled = dashboardService.downsampleBuffer(data.audioData, audioContext.sampleRate, 16000);
        const pcm = dashboardService.floatTo16BitPCM(downsampled);
        socketRef.current?.emit('audio_stream', new Uint8Array(pcm));
      };
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;
    } catch (err) {
      console.error('Microphone error:', err);
    }

    updateIntervalRef.current = setInterval(() => {
      if (transcriptRef.current.trim()) {
        onTranscriptUpdate?.(transcriptRef.current.trim());
        socketRef.current?.emit('context_update', { text: transcriptRef.current });
      }
    }, 3000);

    await playGreeting();
  }, [isRecording, playGreeting, onTranscriptUpdate]);

  const handleEndCall = useCallback(async () => {
    if (!isRecording) return;
    setIsRecording(false);
    setCallStatus('ended');
    onCallStatusChange?.(false);
    clearInterval(updateIntervalRef.current);
    socketRef.current?.emit('stop_audio');
    try {
      processorRef.current?.port.postMessage({ terminate: true });
      processorRef.current?.disconnect();
      processorRef.current = null;
      mediaStreamRef.current?.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
    } catch (e) { console.error('Cleanup error:', e); }
    if (transcriptRef.current.trim()) onTranscriptUpdate?.(transcriptRef.current.trim());
  }, [isRecording, onTranscriptUpdate]);

  const lastLine = lines[lines.length - 1] ?? null;
  const activeText = interimLine || (isSpeaking || isBotThinking ? lastLine?.text : null) || lastLine?.text || null;
  const activeSpeaker = interimLine ? 'Customer' : lastLine?.speaker ?? null;

  return (
    <div className="h-full flex gap-4 min-h-0">

      {/* ── LEFT: Transcript ── */}
      <div className="w-[44%] flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden min-h-0">

        {/* Header */}
        <div className="bg-[#1a3a6b] px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">CAP Credit Union</p>
              <p className="text-white text-xs">AI Voice Callback — Live</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTtsEnabled(v => !v)}
              className="p-1.5 rounded-lg bg-gray hover:bg-gray transition-colors"
              title={ttsEnabled ? 'Mute bot voice' : 'Unmute bot voice'}
            >
              {ttsEnabled
                ? <Volume2 className="w-4 h-4 text-white" />
                : <VolumeX className="w-4 h-4 text-white" />
              }
            </button>
            <span className={`flex items-center gap-1.5 text-xs border px-2.5 py-1 rounded-full ${
              callStatus === 'active' ? 'bg-green-500/20 text-green-300 border-green-400/30'
              : callStatus === 'ended' ? 'bg-gray-500/20 text-gray-300 border-gray-400/30'
              : 'bg-blue-500/20 text-primaryLight border-blue-400/30'
            }`}>
              {callStatus === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
              {callStatus === 'active' ? 'Live' : callStatus === 'ended' ? 'Ended' : 'Ready'}
            </span>
          </div>
        </div>

        {/* Fraud banner */}
        {showFraudBanner && (
          <div className="flex items-start gap-3 bg-red-50 border-b border-red-200 px-4 py-3 flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">Fraud Alert Detected</p>
              <p className="text-xs text-red-600 mt-0.5">Unauthorized transaction reported — escalation may be required</p>
            </div>
          </div>
        )}

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 min-h-0">

          {/* Web chat history */}
          {chatHistoryLines.length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  <MessageSquare className="w-3 h-3" /> Web Chat
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="space-y-2 opacity-60">
                {chatHistoryLines.map(line => {
                  const isAgent = line.speaker === 'Agent';
                  return (
                    <div
                      key={line.id}
                      className="rounded-xl px-3 py-2 text-xs"
                      style={{
                        background: isAgent ? 'rgba(26,58,107,0.06)' : 'rgba(3,105,161,0.06)',
                        border: `1px solid ${isAgent ? 'rgba(26,58,107,0.12)' : 'rgba(3,105,161,0.12)'}`,
                      }}
                    >
                      <span className="font-semibold" style={{ color: isAgent ? '#1a3a6b' : '#0369a1' }}>
                        {isAgent ? 'Agent' : 'Miss Wallace'}
                      </span>
                      <span className="text-gray-500 ml-2">{line.text}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[10px] font-semibold text-purple-400 uppercase tracking-wide flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Voice Call Begins
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </>
          )}

          {lines.map(line => {
            const style = SPEAKER_STYLES[line.speaker] ?? SPEAKER_STYLES['AI Voice'];
            const isBot = line.speaker === 'AI Voice';
            return (
              <div
                key={line.id}
                className="rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: style.bg, border: `1px solid ${style.border}` }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: style.text }}>
                    {isBot
                      ? <><BotIcon className="w-3.5 h-3.5" />AI Voice</>
                      : <><PersonIcon className="w-3.5 h-3.5" />Miss Wallace</>
                    }
                  </span>
                  <span className="text-xs text-gray-400">{line.ts}</span>
                </div>
                <p className="text-gray-800 leading-relaxed">{line.text}</p>
              </div>
            );
          })}

          {interimLine && callStatus === 'active' && (
            <div className="rounded-xl px-3.5 py-2.5 border border-dashed border-sky-200 bg-sky-50/50 text-sm">
              <p className="text-xs font-semibold text-sky-500 mb-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                Listening...
              </p>
              <p className="text-gray-500 italic">{interimLine}</p>
            </div>
          )}

          {(isBotThinking || isSpeaking) && (
            <div className="flex items-center gap-2 text-sm text-purple-600">
              <Mic className="w-4 h-4 animate-pulse" />
              <span className="italic">{isBotThinking ? 'AI thinking...' : 'AI Voice speaking...'}</span>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Call controls */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
          {callStatus === 'active' && (
            <button
              onClick={handleEndCall}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <PhoneOff className="w-4 h-4" />
              End Call
            </button>
          )}
          {callStatus === 'ended' && (
            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">Call Ended</span>
          )}
          {callStatus === 'active' && (
            <p className="text-xs text-gray-400 italic">Listening for customer...</p>
          )}
        </div>
      </div>

      {/* ── RIGHT: Live Call Visual ── */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-[#1a3a6b] to-[#0a1430] rounded-2xl overflow-hidden relative min-h-0">
        <style>{`
          @keyframes avatar-ring {
            0%   { transform: scale(1);   opacity: 0.5; }
            100% { transform: scale(2.2); opacity: 0;   }
          }
          @keyframes soundbar {
            0%, 100% { transform: scaleY(0.4); }
            50%       { transform: scaleY(1);   }
          }
          @keyframes bubble-in {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0);   }
          }
        `}</style>

        {/* Subtle radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(124,58,237,0.12) 0%, transparent 65%)'
        }} />

        {/* Main avatar area */}
        <div className="flex-1 flex items-center justify-around px-6 relative z-10">

          {/* Bot avatar */}
          <div className="flex flex-col items-center gap-5">
            <div className="relative flex items-center justify-center">
              {/* Speaking rings */}
              {(isSpeaking || isBotThinking) && [0, 0.5, 1].map(delay => (
                <div key={delay} className="absolute rounded-full border border-purple-400/40"
                  style={{ width: 96, height: 96,
                    animation: `avatar-ring 1.8s ease-out ${delay}s infinite` }} />
              ))}
              <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                isSpeaking ? 'bg-purple-500/30 border-purple-400/60 shadow-lg shadow-purple-500/20'
                : isBotThinking ? 'bg-purple-500/20 border-purple-400/40'
                : 'bg-white/10 border-white/20'
              }`}>
                <BotIcon className={`w-11 h-11 ${isSpeaking || isBotThinking ? 'text-purple-300' : 'text-white'}`} />
              </div>
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-sm">AI Voice</p>
              <p className={`text-xs mt-0.5 transition-colors ${
                isSpeaking ? 'text-purple-300' : isBotThinking ? 'text-purple-400/70 italic' : 'text-white'
              }`}>
                {isSpeaking ? 'Speaking' : isBotThinking ? 'Processing…' : 'Listening'}
              </p>
            </div>
            {/* Soundbars when speaking */}
            {isSpeaking && (
              <div className="flex items-end gap-0.5 h-5">
                {[0.6, 0.9, 1.1, 0.7, 1.0, 0.8, 0.5].map((s, i) => (
                  <div key={i} className="w-1 rounded-full bg-purple-400"
                    style={{ height: `${s * 20}px`,
                      animation: `soundbar ${0.4 + i * 0.06}s ease-in-out ${i * 0.07}s infinite` }} />
                ))}
              </div>
            )}
          </div>

          {/* Center divider */}
          <div className="flex flex-col items-center gap-3 select-none">
            <div className="w-px h-16 bg-white/10" />
            <div className="flex flex-col items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white text-[10px] font-semibold uppercase tracking-widest">Live</span>
            </div>
            {isRecording && (
              <p className="text-white text-xs font-mono">{formatDuration(callDuration)}</p>
            )}
            <div className="w-px h-16 bg-white/10" />
          </div>

          {/* Customer avatar */}
          <div className="flex flex-col items-center gap-5">
            <div className="relative flex items-center justify-center">
              {/* Listening rings */}
              {!!interimLine && [0, 0.5, 1].map(delay => (
                <div key={delay} className="absolute rounded-full border border-sky-400/40"
                  style={{ width: 96, height: 96,
                    animation: `avatar-ring 1.8s ease-out ${delay}s infinite` }} />
              ))}
              <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                interimLine ? 'bg-sky-500/30 border-sky-400/60 shadow-lg shadow-sky-500/20'
                : 'bg-white/10 border-white/20'
              }`}>
                <PersonIcon className={`w-11 h-11 ${interimLine ? 'text-sky-300' : 'text-white'}`} />
              </div>
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-sm">Miss Wallace</p>
              <p className={`text-xs mt-0.5 transition-colors ${
                interimLine ? 'text-sky-300' : 'text-white'
              }`}>
                {interimLine ? 'Speaking' : 'Listening'}
              </p>
            </div>
            {/* Soundbars when customer is speaking */}
            {!!interimLine && (
              <div className="flex items-end gap-0.5 h-5">
                {[0.5, 0.8, 1.1, 0.9, 0.6, 1.0, 0.7].map((s, i) => (
                  <div key={i} className="w-1 rounded-full bg-sky-400"
                    style={{ height: `${s * 20}px`,
                      animation: `soundbar ${0.4 + i * 0.06}s ease-in-out ${i * 0.07}s infinite` }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Current / last spoken line */}
        {activeText && (
          <div className="px-6 pb-4 relative z-10" key={activeText.slice(0, 20)}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-4 border border-white/10"
              style={{ animation: 'bubble-in 0.3s ease-out both' }}>
              <p className="text-white text-[10px] font-semibold uppercase tracking-wide mb-1.5">
                {activeSpeaker === 'AI Voice' ? 'AI Voice' : 'Miss Wallace'}
                {interimLine ? ' — speaking' : ''}
              </p>
              <p className="text-white text-sm leading-relaxed">
                {interimLine || activeText}
              </p>
            </div>
          </div>
        )}

        {/* Fraud alert bar */}
        {showFraudBanner && (
          <div className="flex items-center gap-3 bg-red-500/20 border-t border-red-500/30 px-5 py-3 flex-shrink-0 relative z-10">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-xs font-semibold">Fraud alert detected — escalation in progress</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceCallbackPanel;
